import { Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../provider/authProvider";
import * as React from 'react';

import useForm from '../hooks/useForm.js'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { MuiMaterials } from '../@index.js';
import * as Assets from "../assets/@index";
import * as global from '../data/global';
import * as Components from '../components/@index.js';
import axios from "axios";
import { createApiEndpoint } from '../index.js';
import { Spinner, SITE_WINDOW_IDS, CCR_DEFAULT_ASSET_TAG, SITE_WINDOW_OPEN_EVENT, ALL_SITES_OPTION, getWindowChromeThemeColor, BRAND_GOLD, BRAND_GOLD_BORDER, brandGoldMetallicTextSx, outlinedActionButtonSx, comparisonFieldSx, openSiteWindow, getMainMenuSiteItems, ModalChrome, ModalBody, ModalContentTile, withScrollHintMenuProps } from '../components/windowChrome/index.js';

/** Routes that cover the viewport (fixed overlay) and must not use pull-to-refresh. */
const FULLSCREEN_APP_ROUTES = new Set(["/site3d", "/siteLayout", "/manufacturing", "/bin"]);
const isFullscreenAppRoute = (pathname) => FULLSCREEN_APP_ROUTES.has(pathname);

/** Full-page Site XXXX tools (not modals). Title-nav must not stack these under each other or under modals. */
const SITE_TOOL_ROUTES = new Set([
    "/site3d",
    "/siteSetup",
    "/cableHealth",
    "/siteLayout",
    "/scan",
    "/diagnostics",
    "/moduleViewer",
    "/devices",
    "/temperatureHistory",
    "/carbonDioxide",
    "/bin",
]);

const isSiteToolRoute = (pathname) => SITE_TOOL_ROUTES.has(pathname);

/** Pull-to-refresh only on the sites list — never on Site XXXX windows or modals. */
const PULL_TO_REFRESH_ROUTES = new Set(["/", "/sites"]);
const isPullToRefreshRoute = (pathname) => PULL_TO_REFRESH_ROUTES.has(pathname);

const PTR_IGNORE_SELECTOR = [
    "input",
    "textarea",
    "select",
    "button",
    "a",
    "label",
    "form",
    ".MuiButtonBase-root",
    ".MuiModal-root",
    ".MuiMenu-root",
    ".MuiPopover-root",
    ".MuiDialog-root",
    "[role='dialog']",
    "[role='menu']",
    "[role='listbox']",
].join(",");

const shouldIgnorePullToRefresh = (target) => {
    if (!target || typeof target.closest !== "function") return false;
    return Boolean(target.closest(PTR_IGNORE_SELECTOR));
};

const readOutletScrollTop = (outletContainer) => {
    if (!outletContainer) return 0;
    return outletContainer.scrollTop || 0;
};

const restoreOutletScrollOverflow = (outletContainer) => {
    if (!outletContainer) return;
    const originalOverflowY = outletContainer.getAttribute("data-original-overflow-y");
    const originalOverflowX = outletContainer.getAttribute("data-original-overflow-x");
    const originalOverflow = outletContainer.getAttribute("data-original-overflow");
    outletContainer.style.overflowY = originalOverflowY || "auto";
    outletContainer.style.overflowX = originalOverflowX || "hidden";
    outletContainer.style.overflow = originalOverflow || "";
    outletContainer.removeAttribute("data-original-overflow-y");
    outletContainer.removeAttribute("data-original-overflow-x");
    outletContainer.removeAttribute("data-original-overflow");

    let parent = outletContainer.parentElement;
    let parentLevel = 0;
    while (parent && parent !== document.body && parentLevel < 3) {
        const parentOverflow = parent.getAttribute("data-original-overflow");
        if (parentOverflow) {
            parent.style.overflow = parentOverflow;
            parent.removeAttribute("data-original-overflow");
        }
        parent = parent.parentElement;
        parentLevel++;
    }
};

const createResetPtrState = () => ({
    isPulling: false,
    armed: false,
    pullDistance: 0,
    startY: 0,
    currentY: 0,
    initialScrollTop: 0,
});

// Helper function to get theme-aware colors
const getThemeColor = (lightColor, darkColor) => {
    if (global.gw.theme === 'light') {
        return lightColor;
    }
    return darkColor;
};

/** Matches `GrainWaveRole` / `AI/GrainWaveRole_Schema.sql` — used when `fetchGrainWaveRoles` is empty or fails. */
const DEFAULT_GRAIN_WAVE_ROLES = [
    { grainWaveRoleId: 1, name: 'Administrator' },
    { grainWaveRoleId: 2, name: 'User' },
    { grainWaveRoleId: 3, name: 'ReadOnly' },
];

// Create a function to generate site styles based on current theme state
/** Same paper / pageBg / border tokens WindowChrome and ModalChrome use. */
const getMainHeaderChromeColors = () => ({
    paper: getWindowChromeThemeColor('#ffffff', '#2d2d2d'),
    pageBg: getWindowChromeThemeColor('#e8e8e8', '#1a1a1a'),
    border: getWindowChromeThemeColor('#cccccc', '#f4cc8e4d'),
});

const getSiteStyles = () => {
    const { paper, pageBg, border } = getMainHeaderChromeColors();
    return {
        sitesContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            boxSizing: 'border-box',
            height: '100vh',
            maxHeight: '100vh',
            '@supports (height: 100dvh)': {
                height: '100dvh',
                maxHeight: '100dvh',
            },
            overflow: 'hidden',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            // Match ModalChrome / WindowChrome body canvas.
            backgroundColor: pageBg,
            color: getWindowChromeThemeColor('#333333', '#f4cc8e'),
        },
        // Match WindowChrome / ModalChrome header row (paper + bottom border + px/py),
        // without site/tool selects or a close button.
        menuBar: {
            appBar: {
                // Defeat MUI AppBar `color="primary"` / Paper defaults so chrome colors stick.
                backgroundColor: `${paper} !important`,
                backgroundImage: 'none !important',
                boxShadow: 'none !important',
                borderBottom: `1px solid ${border} !important`,
                color: getWindowChromeThemeColor('#333333', '#f4cc8e'),
                zIndex: 500,
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
            },
            toolBar: {
                margin: 0,
                px: 2.5,
                py: 1.5,
                minHeight: 'unset !important',
                gap: 1,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'transparent',
            },
        },
    };
};

export const ProtectedRoute = () => {
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState([false]);
    const [feedbackText, setFeedbackText] = React.useState('');
    const [isProfileModalOpen, setIsProfileModalOpen] = React.useState([false]);
    const [isAdminModalOpen, setIsAdminModalOpen] = React.useState([false]);
    const [isSitesAndRolesModalOpen, setIsSitesAndRolesModalOpen] = React.useState([false]);
    const [isSiteFilterModalOpen, setIsSiteFilterModalOpen] = React.useState([false]);
    const [isSiteGradesModalOpen, setIsSiteGradesModalOpen] = React.useState([false]);
    /** When set, Site Grades opens already loaded for this site (title-nav / site card). Cleared when modal closes. */
    const [siteGradesInitialGuid, setSiteGradesInitialGuid] = React.useState(null);
    const [isSiteComparisonModalOpen, setIsSiteComparisonModalOpen] = React.useState([false]);
    /** When set, Site Comparison opens already loaded for this site. Cleared when modal closes. */
    const [siteComparisonInitialGuid, setSiteComparisonInitialGuid] = React.useState(null);
    const [isSiteInventoryModalOpen, setIsSiteInventoryModalOpen] = React.useState([false]);
    /** When set, Site Inventory opens scoped to one bin (from site card). Cleared when modal closes. */
    const [siteInventorySingleBin, setSiteInventorySingleBin] = React.useState(null);
    const [isSiteVersioningModalOpen, setIsSiteVersioningModalOpen] = React.useState([false]);
    /** When set, Site Versioning opens scoped to one site (from site card). Cleared when modal closes. */
    const [siteVersioningSiteGuid, setSiteVersioningSiteGuid] = React.useState(null);
    const [isSitesScanningModalOpen, setIsSitesScanningModalOpen] = React.useState([false]);
    const [isRemoteModalOpen, setIsRemoteModalOpen] = React.useState([false]);
    const [remoteSiteGuid, setRemoteSiteGuid] = React.useState(null);
    /** When set, Sites Scanning opens filtered to this site. Cleared when modal closes. */
    const [sitesScanningSiteGuid, setSitesScanningSiteGuid] = React.useState(null);
    const [isViewSiteIssuesModalOpen, setIsViewSiteIssuesModalOpen] = React.useState([false]);
    /** When set, Site Issues opens scoped to one site (from site card). Cleared when modal closes. */
    const [viewSiteIssuesSiteGuid, setViewSiteIssuesSiteGuid] = React.useState(null);
    const [users, setUsers] = React.useState([]);
    /** Cached admin email list — avoids refetch; cleared after a successful Admin save. Never use `userAttributes` here (it runs RefreshLastScan and can take 20s+). */
    const adminUserEmailsCacheRef = React.useRef(null);
    const [grainWaveRoles, setGrainWaveRoles] = React.useState([]);
    const [senderEmail, setSenderEmail] = React.useState(global.gw.email);
    const [userSelectedAllSites, setUserSelectedAllSites] = React.useState([]);
    const [adminSelectedSiteIds, setAdminSelectedSiteIds] = React.useState([]);
    const adminSelectedSiteIdsRef = React.useRef([]);
    /** Bumps when site loads are invalidated or the admin changes the Sites picker (prevents stale getUserSelectedAllSites from overwriting selection). */
    const sitesLoadGenerationRef = React.useRef(0);
    /** Ignores stale loadAdminUserProfileByEmail results when switching users quickly. */
    const adminProfileLoadGenerationRef = React.useRef(0);
    /** Drops an in-flight profile save when a newer Update starts (password encrypt is async). */
    const adminProfileSaveGenerationRef = React.useRef(0);
    const [forceUpdate, setForceUpdate] = React.useState(0);
    const [isLogoutNavigation, setIsLogoutNavigation] = React.useState(false);
    const [currentTheme, setCurrentTheme] = React.useState(global.gw.theme || 'dark');

    // Pull-to-refresh state
    const [pullToRefresh, setPullToRefresh] = React.useState({
        isPulling: false,
        armed: false,
        pullDistance: 0,
        startY: 0,
        currentY: 0,
        initialScrollTop: 0 // Track scroll position when touch starts
    });
    const pullToRefreshRef = React.useRef(pullToRefresh);
    const scrollTrackingRef = React.useRef({
        lastScrollTop: 0,
        lastScrollTime: Date.now(), // Initialize to current time instead of 0
        wasScrolling: false,
        initialScrollTopOnRoute: 0, // Track scroll position when route loads
        maxScrollTopReached: 0 // Track the maximum scroll position reached (to detect if user scrolled away from top)
    });

    // Keep ref in sync with state
    React.useEffect(() => {
        pullToRefreshRef.current = pullToRefresh;
    }, [pullToRefresh]);

    React.useEffect(() => {
        const onOpenBinInv = (e) => {
            const d = e.detail || {};
            const siteGuid = d.siteGuid;
            if (!siteGuid) return;
            const binIdRaw = d.binId;
            if (binIdRaw == null || binIdRaw === "") {
                setSiteInventorySingleBin({ siteGuid });
            } else {
                const n = typeof binIdRaw === "number" ? binIdRaw : parseInt(binIdRaw, 10);
                if (Number.isNaN(n)) return;
                setSiteInventorySingleBin({ siteGuid, binId: n });
            }
            setIsSiteInventoryModalOpen([true]);
        };
        window.addEventListener("grainwave-open-site-inventory-bin", onOpenBinInv);
        return () => window.removeEventListener("grainwave-open-site-inventory-bin", onOpenBinInv);
    }, []);

    React.useEffect(() => {
        if (!isSiteInventoryModalOpen[0]) setSiteInventorySingleBin(null);
    }, [isSiteInventoryModalOpen[0]]);

    React.useEffect(() => {
        const onOpenSiteIssues = (e) => {
            if (!global.isAdminUser(global.gw.email)) return;
            const siteGuid = e.detail?.siteGuid;
            if (!siteGuid) return;
            setViewSiteIssuesSiteGuid(siteGuid);
            setIsViewSiteIssuesModalOpen([true]);
        };
        window.addEventListener("grainwave-open-site-issues", onOpenSiteIssues);
        return () => window.removeEventListener("grainwave-open-site-issues", onOpenSiteIssues);
    }, []);

    React.useEffect(() => {
        if (!isViewSiteIssuesModalOpen[0]) setViewSiteIssuesSiteGuid(null);
    }, [isViewSiteIssuesModalOpen[0]]);

    React.useEffect(() => {
        const onOpenSiteVersioning = (e) => {
            const siteGuid = e.detail?.siteGuid;
            if (!siteGuid) return;
            setSiteVersioningSiteGuid(siteGuid);
            setIsSiteVersioningModalOpen([true]);
        };
        window.addEventListener("grainwave-open-site-versioning", onOpenSiteVersioning);
        return () => window.removeEventListener("grainwave-open-site-versioning", onOpenSiteVersioning);
    }, []);

    React.useEffect(() => {
        if (!isSiteVersioningModalOpen[0]) setSiteVersioningSiteGuid(null);
    }, [isSiteVersioningModalOpen[0]]);

    React.useEffect(() => {
        if (!isRemoteModalOpen[0]) setRemoteSiteGuid(null);
    }, [isRemoteModalOpen[0]]);

    React.useEffect(() => {
        if (!isSitesScanningModalOpen[0]) setSitesScanningSiteGuid(null);
    }, [isSitesScanningModalOpen[0]]);

    React.useEffect(() => {
        if (!isSiteComparisonModalOpen[0]) setSiteComparisonInitialGuid(null);
    }, [isSiteComparisonModalOpen[0]]);

    React.useEffect(() => {
        if (!isSiteGradesModalOpen[0]) setSiteGradesInitialGuid(null);
    }, [isSiteGradesModalOpen[0]]);

    // Initialize theme on component mount
    React.useEffect(() => {
        // Restore theme from localStorage if available
        try {
            const savedTheme = localStorage.getItem('grainwave_theme');
            if (savedTheme === 'light' || savedTheme === 'dark') {
                global.gw.theme = savedTheme;
                // Apply theme class to body immediately
                if (savedTheme === 'light') {
                    document.body.classList.add('light-mode');
                } else {
                    document.body.classList.remove('light-mode');
                }
            } else if (!global.gw.theme) {
                // Default to dark if no saved theme
                global.gw.theme = 'dark';
                document.body.classList.remove('light-mode');
            }
        } catch (error) {
            console.error('Error restoring theme from localStorage:', error);
        if (!global.gw.theme) {
            global.gw.theme = 'dark';
                document.body.classList.remove('light-mode');
        }
        }
        setForceUpdate(prev => prev + 1);
    }, []);
    const { clearToken, authReady } = useAuth();
    const navigate = useNavigate();  //  DO NOT REMOVE!
    const location = useLocation();
    const isAdmin = global.isAdminUser(global.gw.email);

    // Title-click Site XXXX switcher (SiteWindowTitle → openSiteWindow).
    React.useEffect(() => {
        const onOpenSiteWindow = (e) => {
            const windowId = e.detail?.windowId;
            const siteGuid = e.detail?.siteGuid || null;
            if (!windowId) return;

            const admin = global.isAdminUser(global.gw.email);
            if (
                (windowId === SITE_WINDOW_IDS.issues
                    || windowId === SITE_WINDOW_IDS.versioning
                    || windowId === SITE_WINDOW_IDS.sitesScanning
                    || windowId === SITE_WINDOW_IDS.layout
                    || windowId === SITE_WINDOW_IDS.setup
                    || windowId === SITE_WINDOW_IDS.diagnostics
                    || windowId === SITE_WINDOW_IDS.moduleViewer
                    || windowId === SITE_WINDOW_IDS.remote)
                && !admin
            ) {
                return;
            }

            setIsSiteComparisonModalOpen([false]);
            setIsSiteFilterModalOpen([false]);
            setIsSiteGradesModalOpen([false]);
            setIsSiteInventoryModalOpen([false]);
            setIsViewSiteIssuesModalOpen([false]);
            setIsSiteVersioningModalOpen([false]);
            setIsSitesScanningModalOpen([false]);
            setIsRemoteModalOpen([false]);
            setSiteInventorySingleBin(null);
            setViewSiteIssuesSiteGuid(null);
            setSiteVersioningSiteGuid(null);
            setSitesScanningSiteGuid(null);
            setRemoteSiteGuid(null);
            setSiteComparisonInitialGuid(null);
            setSiteGradesInitialGuid(null);

            // Leaving a full-page Site tool for a modal: drop the route first so Close
            // on the modal returns to /sites — not another Site window underneath.
            const leaveSiteToolRouteForModal = () => {
                if (isSiteToolRoute(location.pathname)) {
                    global.clearSite3dPinned?.();
                    navigate("/sites", { replace: true });
                }
            };

            switch (windowId) {
                case SITE_WINDOW_IDS.comparison:
                    leaveSiteToolRouteForModal();
                    setSiteComparisonInitialGuid(siteGuid);
                    setIsSiteComparisonModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.filter:
                    leaveSiteToolRouteForModal();
                    setIsSiteFilterModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.grades:
                    leaveSiteToolRouteForModal();
                    setSiteGradesInitialGuid(siteGuid);
                    setIsSiteGradesModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.cableHealth:
                    navigate("/cableHealth", {
                        replace: true,
                        state: siteGuid ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.history:
                    navigate("/temperatureHistory", {
                        replace: true,
                        state: siteGuid ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.scan:
                    navigate("/scan", {
                        replace: true,
                        state: siteGuid && siteGuid !== ALL_SITES_OPTION ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.diagnostics:
                    navigate("/diagnostics", {
                        replace: true,
                        state: siteGuid && siteGuid !== ALL_SITES_OPTION ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.moduleViewer:
                    navigate("/moduleViewer", {
                        replace: true,
                        state: siteGuid && siteGuid !== ALL_SITES_OPTION ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.devices:
                    navigate("/devices", {
                        replace: true,
                        state: siteGuid ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.co2:
                    navigate("/carbonDioxide", {
                        replace: true,
                        state: siteGuid ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.inventory:
                    leaveSiteToolRouteForModal();
                    if (siteGuid) setSiteInventorySingleBin({ siteGuid });
                    setIsSiteInventoryModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.issues:
                    leaveSiteToolRouteForModal();
                    if (siteGuid) setViewSiteIssuesSiteGuid(siteGuid);
                    setIsViewSiteIssuesModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.versioning:
                    leaveSiteToolRouteForModal();
                    if (siteGuid) setSiteVersioningSiteGuid(siteGuid);
                    setIsSiteVersioningModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.sitesScanning:
                    leaveSiteToolRouteForModal();
                    if (siteGuid) setSitesScanningSiteGuid(siteGuid);
                    setIsSitesScanningModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.remote:
                    leaveSiteToolRouteForModal();
                    setRemoteSiteGuid(siteGuid || null);
                    setIsRemoteModalOpen([true]);
                    break;
                case SITE_WINDOW_IDS.layout:
                    navigate("/siteLayout", {
                        replace: true,
                        state: siteGuid
                            ? { loadSiteForLayout: true, siteGuid }
                            : { openSitePicker: true },
                    });
                    break;
                case SITE_WINDOW_IDS.setup:
                    navigate("/siteSetup", {
                        replace: true,
                        state: siteGuid ? { siteGuid, autoLoad: true } : {},
                    });
                    break;
                case SITE_WINDOW_IDS.ccr: {
                    const infos = global.gw.siteInfos || [];
                    const carried = infos.find((s) => s.siteGuid === siteGuid && s.isCcr);
                    const target = carried?.siteGuid || infos.find((s) => s.isCcr)?.siteGuid || null;
                    if (target) navigate(`/ccr/${target}/${encodeURIComponent(CCR_DEFAULT_ASSET_TAG)}`, { replace: true });
                    break;
                }
                case SITE_WINDOW_IDS.sites3d:
                case SITE_WINDOW_IDS.bin: {
                    const isBin = windowId === SITE_WINDOW_IDS.bin;
                    const toolPath = isBin ? "/bin" : "/site3d";
                    if (siteGuid) {
                        const info = (global.gw.siteInfos || []).find(
                            (s) => s.siteGuid === siteGuid,
                        );
                        if (info && info.siteGuid !== global.gw.selectedSite?.siteGuid) {
                            if (global.gw.selectedSite?.refreshId) {
                                clearTimeout(global.gw.selectedSite.refreshId);
                            }
                            global.gw.selectedSite = new global.Site();
                            global.gw.selectedSite.siteIndex = info.siteIndex;
                            global.gw.selectedSite.siteGuid = info.siteGuid;
                            global.gw.selectedSite.siteName = info.siteName;
                            global.gw.selectedSite.lastScan = info.lastScan;
                            global.gw.selectedSite.isSiteInitialized = false;
                            global.saveSelectedSite();
                        } else if (info && !global.gw.selectedSite?.siteGuid) {
                            global.gw.selectedSite.siteIndex = info.siteIndex;
                            global.gw.selectedSite.siteGuid = info.siteGuid;
                            global.gw.selectedSite.siteName = info.siteName;
                            global.gw.selectedSite.lastScan = info.lastScan;
                            global.saveSelectedSite();
                        }
                        if (isBin && global.gw.selectedSite) {
                            global.gw.selectedSite.selectedBinId = 0;
                            global.gw.selectedSite.selectedBinName = "";
                        }
                        if (global.gw.weatherStationAbortController) {
                            global.gw.weatherStationAbortController.abort();
                            global.gw.weatherStationAbortController = null;
                        }
                        if (global.gw.dashboardInterval) {
                            clearInterval(global.gw.dashboardInterval);
                            global.gw.dashboardInterval = null;
                        }
                        if (!isBin) {
                            global.markSite3dPinned?.();
                        }
                        navigate(toolPath, {
                            replace: true,
                            state: { siteGuid, autoLoad: true },
                        });
                    } else {
                        navigate(toolPath, {
                            replace: true,
                            state: { openSitePicker: true },
                        });
                    }
                    break;
                }
                default:
                    break;
            }
        };
        window.addEventListener(SITE_WINDOW_OPEN_EVENT, onOpenSiteWindow);
        return () => window.removeEventListener(SITE_WINDOW_OPEN_EVENT, onOpenSiteWindow);
    }, [navigate, location.pathname]);

    React.useEffect(() => {
        if (!isAdminModalOpen[0] || !isAdmin || !global.gw.token) return;
        axios({
            url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + global.gw.token,
            },
            data: {
                query: `query { fetchGrainWaveRoles { grainWaveRoleId name } }`,
            },
        })
            .then((r) => {
                const d = r.data?.data?.fetchGrainWaveRoles;
                if (!Array.isArray(d) || d.length === 0) return;
                const normalized = d
                    .map((x) => ({
                        grainWaveRoleId: x.grainWaveRoleId ?? x.GrainWaveRoleId,
                        name: x.name ?? x.Name,
                    }))
                    .filter((x) => x.grainWaveRoleId != null && x.name);
                if (normalized.length > 0) setGrainWaveRoles(normalized);
            })
            .catch(() => {});
    }, [isAdminModalOpen[0], isAdmin]);

    // Track scroll position to detect if user was recently scrolling
    React.useEffect(() => {
        const outletContainer = document.getElementById('outletContainer');
        if (!outletContainer) {
            return;
        }

        // Check which element is actually scrollable
        const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const containerScrollTop = outletContainer.scrollTop;
        
        // Get actual visual scroll position using getBoundingClientRect on first child
        // Negative rect.top means content has been scrolled up (content top is above viewport)
        // Positive rect.top means content is below viewport top (at top or not scrolled)
        let actualScrollTop = 0;
        if (outletContainer.children && outletContainer.children.length > 0) {
            const rect = outletContainer.children[0].getBoundingClientRect();
            // If rect.top is negative, content is scrolled up - convert to positive scroll value
            // If rect.top is positive or 0, we're at the top - scroll is 0
            actualScrollTop = Math.max(0, -rect.top);
        }

        // Initialize scroll position when route changes
        const tracking = scrollTrackingRef.current;
        // Track max scroll for detection (use the maximum of both)
        const maxScrollTop = Math.max(containerScrollTop, windowScrollTop);
        // For pull-to-refresh, we use actual scroll position from getBoundingClientRect
        tracking.lastScrollTop = maxScrollTop;
        tracking.lastScrollTime = Date.now();
        tracking.initialScrollTopOnRoute = actualScrollTop; // Use actual scroll position for pull-to-refresh check
        tracking.maxScrollTopReached = Math.max(actualScrollTop, maxScrollTop); // Reset max scroll on route change (for detection)
        tracking.wasScrolling = false; // Reset on route change

        const handleScroll = () => {
            const now = Date.now();
            const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const containerScrollTop = outletContainer.scrollTop;
            
            // Get actual visual scroll position using getBoundingClientRect on first child
            // Negative rect.top means content has been scrolled up (content top is above viewport)
            // Positive rect.top means content is below viewport top (at top or not scrolled)
            let actualScrollTop = 0;
            if (outletContainer.children && outletContainer.children.length > 0) {
                const rect = outletContainer.children[0].getBoundingClientRect();
                // If rect.top is negative, content is scrolled up - convert to positive scroll value
                // If rect.top is positive or 0, we're at the top - scroll is 0
                actualScrollTop = Math.max(0, -rect.top);
            }
            
            // Track whichever is actually scrolling (use the maximum of all)
            const currentScrollTop = Math.max(containerScrollTop, windowScrollTop, actualScrollTop);
            const tracking = scrollTrackingRef.current;
            
            // If scroll position changed, mark as scrolling
            if (currentScrollTop !== tracking.lastScrollTop || actualScrollTop !== tracking.initialScrollTopOnRoute) {
                tracking.wasScrolling = true;
                tracking.lastScrollTime = now;
                // Track maximum scroll position reached (use actual scroll position)
                if (actualScrollTop > tracking.maxScrollTopReached) {
                    tracking.maxScrollTopReached = actualScrollTop;
                }
            }
            tracking.lastScrollTop = currentScrollTop;
        };

        // Listen to both window and container scroll
        window.addEventListener('scroll', handleScroll, { passive: true });
        outletContainer.addEventListener('scroll', handleScroll, { passive: true });
        
        // Also periodically check scroll position (in case scroll events are missed)
        const scrollCheckInterval = setInterval(() => {
            const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const containerScrollTop = outletContainer.scrollTop;
            
            // Get actual visual scroll position using getBoundingClientRect on first child
            // Negative rect.top means content has been scrolled up (content top is above viewport)
            // Positive rect.top means content is below viewport top (at top or not scrolled)
            let actualScrollTop = 0;
            if (outletContainer.children && outletContainer.children.length > 0) {
                const rect = outletContainer.children[0].getBoundingClientRect();
                // If rect.top is negative, content is scrolled up - convert to positive scroll value
                // If rect.top is positive or 0, we're at the top - scroll is 0
                actualScrollTop = Math.max(0, -rect.top);
            }
            
            // Track whichever is actually scrolling (use the maximum of all)
            const currentScrollTop = Math.max(containerScrollTop, windowScrollTop, actualScrollTop);
            const tracking = scrollTrackingRef.current;
            if (actualScrollTop > tracking.maxScrollTopReached) {
                tracking.maxScrollTopReached = actualScrollTop;
                tracking.wasScrolling = true;
                tracking.lastScrollTime = Date.now();
            }
            tracking.lastScrollTop = currentScrollTop;
        }, 100); // Check every 100ms
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            outletContainer.removeEventListener('scroll', handleScroll);
            clearInterval(scrollCheckInterval);
        };
    }, [location.pathname]);

    // Fetch user profile data on component mount
    React.useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const response = await createApiEndpoint().fetchProfile("direct");
                if (response && response.data && response.data.fetchProfile) {
                    // Save to local form values
                    values.email = response.data.fetchProfile.email;
                    values.first = response.data.fetchProfile.first;
                    values.last = response.data.fetchProfile.last;
                    values.phone = response.data.fetchProfile.cell;
                    setSenderEmail(response.data.fetchProfile.email);

                    // Save to global object for persistence across navigation
                    if (!global.gw.userProfile) {
                        global.gw.userProfile = {};
                    }
                    global.gw.userProfile.first = response.data.fetchProfile.first;
                    global.gw.userProfile.last = response.data.fetchProfile.last;
                    global.gw.userProfile.email = response.data.fetchProfile.email;
                    global.gw.userProfile.phone = response.data.fetchProfile.cell;

                    // Save user profile to localStorage for persistence across page refreshes
                    try {
                        localStorage.setItem('grainwave_userProfile', JSON.stringify(global.gw.userProfile));
                    } catch (error) {
                        console.error('Error saving userProfile to localStorage:', error);
                    }
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
            }
        };

        if (global.gw.token) {
            fetchUserProfile();
        }
    }, [global.gw.token, global.gw.isAuthenticated]);

    // Apply theme to body element and update state
    React.useEffect(() => {
        if (global.gw.theme === 'light') {
            document.body.classList.add('light-mode');
        } else {
            document.body.classList.remove('light-mode');
        }
        setCurrentTheme(global.gw.theme || 'dark');
    }, [global.gw.theme]);

    // Listen for theme changes and trigger re-render
    React.useEffect(() => {
        const handleThemeChange = () => {
            // console.log('ProtectedRoute: themeChanged event received, updating forceUpdate');
            setForceUpdate(prev => prev + 1);
        };

        window.addEventListener('themeChanged', handleThemeChange);
        // console.log('ProtectedRoute: Added themeChanged event listener');

        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
        };
    }, []);

    // Also watch for changes in global.gw.themeChanged
    React.useEffect(() => {
        // console.log('ProtectedRoute: global.gw.themeChanged updated, current theme:', global.gw.theme);
        setForceUpdate(prev => prev + 1);
    }, [global.gw.themeChanged]);

    // Direct theme watcher - check theme every 100ms
    React.useEffect(() => {
        // Initialize theme if not set (restore from localStorage first)
        if (!global.gw.theme) {
            try {
                const savedTheme = localStorage.getItem('grainwave_theme');
                if (savedTheme === 'light' || savedTheme === 'dark') {
                    global.gw.theme = savedTheme;
                    global.gw.lastKnownTheme = savedTheme;
                    // Apply theme class to body
                    if (savedTheme === 'light') {
                        document.body.classList.add('light-mode');
                    } else {
                        document.body.classList.remove('light-mode');
                    }
                } else {
                    // Default to dark if no saved theme
            global.gw.theme = 'dark';
            global.gw.lastKnownTheme = 'dark';
                    document.body.classList.remove('light-mode');
                }
            } catch (error) {
                console.error('Error restoring theme from localStorage:', error);
                global.gw.theme = 'dark';
                global.gw.lastKnownTheme = 'dark';
                document.body.classList.remove('light-mode');
            }
            // Force initial render with correct theme
            setForceUpdate(prev => prev + 1);
        } else {
            // If theme is already set, make sure lastKnownTheme matches
            global.gw.lastKnownTheme = global.gw.theme;
        }

        const interval = setInterval(() => {
            const themeValue = global.gw.theme;
            if (themeValue !== global.gw.lastKnownTheme) {
                // console.log('ProtectedRoute: Theme changed from', global.gw.lastKnownTheme, 'to', themeValue);
                global.gw.lastKnownTheme = themeValue;
                setForceUpdate(prev => prev + 1);
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);

    // Pull-to-refresh handlers
    const handleTouchStart = React.useCallback((e) => {
        if (!isPullToRefreshRoute(location.pathname) || isFullscreenAppRoute(location.pathname)) {
            return;
        }

        if (shouldIgnorePullToRefresh(e.target)) {
            return;
        }

        const touchY = e.touches[0].clientY;
        const outletContainer = document.getElementById('outletContainer');
        if (!outletContainer) return;

        const containerScrollTop = readOutletScrollTop(outletContainer);
        if (containerScrollTop > 2) {
            return;
        }

        setPullToRefresh({
            isPulling: false,
            armed: true,
            pullDistance: 0,
            startY: touchY,
            currentY: touchY,
            initialScrollTop: containerScrollTop,
        });
    }, [location.pathname]);

    const handleTouchMove = React.useCallback((e) => {
        if (!isPullToRefreshRoute(location.pathname) || isFullscreenAppRoute(location.pathname)) {
            return;
        }

        if (shouldIgnorePullToRefresh(e.target)) {
            if (pullToRefreshRef.current.armed || pullToRefreshRef.current.isPulling) {
                const outletContainer = document.getElementById('outletContainer');
                if (outletContainer) {
                    outletContainer.style.transform = '';
                    restoreOutletScrollOverflow(outletContainer);
                }
                setPullToRefresh(createResetPtrState());
            }
            return;
        }

        // Pages with fixed-positioned containers that break when outletContainer is transformed
        const pagesWithFixedContainers = [
            '/temperatureComparison',
            '/carbonDioxide',
            '/temperatureHistory',
            '/scan',
            '/diagnostics',
            '/moduleViewer',
            '/devices',
            '/inventory',
            '/manufacturing',
            '/siteLayout',
            '/siteSetup',
            '/cableHealth',
        ];
        const hasFixedContainers = pagesWithFixedContainers.includes(location.pathname);

        // Don't interfere with form inputs - let browser handle autofill
        const target = e.target;
        const isFormElement = target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.closest('input') ||
            target.closest('textarea') ||
            target.closest('select') ||
            target.closest('form');

        if (isFormElement) {
            // If we were pulling, stop it
            const currentState = pullToRefreshRef.current;
            if (currentState.isPulling) {
                setPullToRefresh(prev => ({ ...prev, isPulling: false, pullDistance: 0, initialScrollTop: 0 }));
                const outletContainer = document.getElementById('outletContainer');
                // Pages with fixed-positioned containers that break when outletContainer is transformed
                const pagesWithFixedContainers = [
                    '/temperatureComparison',
                    '/carbonDioxide',
                    '/temperatureHistory',
                    '/scan',
                    '/diagnostics',
                    '/moduleViewer',
                    '/devices',
                    '/inventory',
                    '/manufacturing',
                    '/siteLayout',
                    '/siteSetup',
                    '/cableHealth',
                ];
                const hasFixedContainers = pagesWithFixedContainers.includes(location.pathname);
                if (outletContainer) {
                    if (hasFixedContainers) {
                        // Reset fixed container transforms
                        const fixedContainers = outletContainer.querySelectorAll('[style*="position: fixed"], [class*="reportWindow"], [class*="carbonDioxideWindow"], [class*="graphWindow"], [class*="scanWindow"], [class*="manufacturingWindow"]');
                        fixedContainers.forEach(container => {
                            const computedStyle = window.getComputedStyle(container);
                            if (computedStyle.position === 'fixed') {
                                container.style.transform = '';
                                container.style.transition = 'transform 0.3s ease';
                            }
                        });
                    } else {
                        outletContainer.style.transform = '';
                        outletContainer.style.transition = 'transform 0.3s ease';
                        // Restore scrolling
                        outletContainer.style.overflowY = 'auto';
                        outletContainer.style.overflowX = 'hidden';
                        outletContainer.style.overflow = '';
                    }
                }
            }
            return; // Let browser handle form interactions and autofill
        }

        const outletContainer = document.getElementById('outletContainer');
        if (!outletContainer) return;

        const currentState = pullToRefreshRef.current;
        const currentY = e.touches[0].clientY;
        const currentActualScrollTop = readOutletScrollTop(outletContainer);
        const isAtTop = currentActualScrollTop <= 2;
        const wasAtTopWhenStarted = (currentState.initialScrollTop ?? 0) <= 2;
        const ptrActive = currentState.isPulling || currentState.armed;

        // If we're already pulling, continue with pull-to-refresh
        if (ptrActive) {
            // Stop pulling if we weren't at the top when touch started, or if we've scrolled away from top
            if (!wasAtTopWhenStarted || !isAtTop) {
                setPullToRefresh(createResetPtrState());
                // Reset transforms based on page type
                if (hasFixedContainers) {
                    // Reset fixed container transforms
                    const fixedContainers = outletContainer.querySelectorAll('[style*="position: fixed"], [class*="reportWindow"], [class*="carbonDioxideWindow"], [class*="graphWindow"], [class*="scanWindow"], [class*="manufacturingWindow"]');
                    fixedContainers.forEach(container => {
                        const computedStyle = window.getComputedStyle(container);
                        if (computedStyle.position === 'fixed') {
                            container.style.transform = '';
                            container.style.transition = 'transform 0.3s ease';
                        }
                    });
                } else {
                    outletContainer.style.transform = '';
                    outletContainer.style.transition = 'transform 0.3s ease';
                    // Restore scrolling properly
                    const originalOverflowY = outletContainer.getAttribute('data-original-overflow-y');
                    const originalOverflowX = outletContainer.getAttribute('data-original-overflow-x');
                    if (originalOverflowY) {
                        outletContainer.style.overflowY = originalOverflowY;
                        outletContainer.removeAttribute('data-original-overflow-y');
                    } else {
                        outletContainer.style.overflowY = 'auto';
                    }
                    if (originalOverflowX) {
                        outletContainer.style.overflowX = originalOverflowX;
                        outletContainer.removeAttribute('data-original-overflow-x');
                    } else {
                        outletContainer.style.overflowX = 'hidden';
                    }
                    const originalOverflow = outletContainer.getAttribute('data-original-overflow');
                    if (originalOverflow) {
                        outletContainer.style.overflow = originalOverflow;
                        outletContainer.removeAttribute('data-original-overflow');
                    } else {
                        outletContainer.style.overflow = '';
                    }
                }
                return; // Don't prevent default - allow scrolling
            }

            const pullDistance = Math.max(0, currentY - currentState.startY);

            // Wait for a real downward pull so taps and fling-scrolls stay native.
            if (pullDistance > 16) {
                setPullToRefresh(prev => ({
                    ...prev,
                    isPulling: true,
                    armed: false,
                    currentY: currentY,
                    pullDistance: pullDistance
                }));

                outletContainer.style.transform = `translateY(${pullDistance}px)`;
                outletContainer.style.transition = 'none';
                e.preventDefault();
                return;
            }
            // If pullDistance is 0 or negative, allow normal scrolling
            return;
        }

        // Don't start pull-to-refresh during touch move - it should only start in handleTouchStart when at the top
        // This prevents pull-to-refresh from triggering when scrolling back up after scrolling down
        // If not at top, allow normal scrolling (don't prevent default)
    }, [location.pathname]);

    const handleTouchEnd = React.useCallback(() => {
        if (!isPullToRefreshRoute(location.pathname) || isFullscreenAppRoute(location.pathname)) {
            return;
        }

        const currentState = pullToRefreshRef.current;
        const outletContainer = document.getElementById('outletContainer');
        if (outletContainer) {
            outletContainer.style.transition = 'transform 0.3s ease';
            outletContainer.style.transform = '';
            restoreOutletScrollOverflow(outletContainer);
        }

        if (currentState.isPulling && currentState.pullDistance >= 200) {
            setPullToRefresh(createResetPtrState());
            window.location.reload();
            return;
        }

        setPullToRefresh(createResetPtrState());
    }, [location.pathname]);

    // Add touch event listeners to detect pull-to-refresh
    // Note: Disabled on fullscreen app routes to avoid interfering with canvas/3D interactions
    React.useEffect(() => {
        if (!isPullToRefreshRoute(location.pathname) || isFullscreenAppRoute(location.pathname)) {
            return;
        }

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd, location.pathname]);

    const spinnerBoxRef = React.useRef(null);
    const authenticatedSpinnerBoxRef = React.useRef(null);

    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange
    } = useForm(() => { return { email: "", first: "", last: "", phone: "", isNewUser: false, sites: [], wall: null, grainWaveRoleId: 2 }; });

    const validate = () => {
        let temp = {};
        temp.email = (/\S+@\S+\.\S+/).test(values.email) ? "" : "Email is not valid.";
        temp.first = (/[A-Za-z]{2,20}/).test(values.first) ? "" : "First name is not valid.";
        temp.last = (/[A-Za-z]{2,30}/).test(values.last) ? "" : "Last name is not valid.";
        temp.phone = values.phone === 0 || values.phone === "" || (/[0-9]{10}/).test(values.phone) ? "" : "Phone number is not valid.";
        temp.wall = (!values.isNewUser && values.wall === "") || (/.{4,20}/).test(values.wall) ? "" : "Password is not valid.";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const invalidateAdminSitesLoad = () => {
        sitesLoadGenerationRef.current += 1;
    };

    const clearForm = () => {
        invalidateAdminSitesLoad();
        setSenderEmail(global.gw.email);
        setValues({
            email: "",
            first: "",
            last: "",
            phone: "",
            isNewUser: false,
            sites: [],
            wall: null,
            grainWaveRoleId: 2,
        });
        setUserSelectedAllSites([]);
        setAdminSelectedSiteIds([]);
        adminSelectedSiteIdsRef.current = [];
        setErrors("");
    };

    const sendUserFeedback = (feedback) => {
        axios({
            url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Bearer " + global.gw.token,
            },
            data: {
                query: `
                        mutation AddUserFeedback($email: String!, $feedback: String!) {
                            addUserFeedback(email: $email, feedback: $feedback)
                        }
                    `,
                variables: {
                    email: global.gw.email,
                    feedback: feedback,
                }
            }
        }).then(response => {
            let testm = 0;
        }).catch(function (error) {
            let teadsfdsafdsaf = 0;
        });
    };

    const adminModalMenuPaperSx = {
        backgroundColor: `${getThemeColor('#ffffff', '#2d2d2d')} !important`,
        '& .MuiMenuItem-root': {
            color: `${getThemeColor('#333333', '#f4cc8e')} !important`,
            backgroundColor: 'transparent !important',
            '&:hover': {
                backgroundColor: `${getThemeColor('#f5f5f5', '#3d3d3d')} !important`,
            },
        },
        '& .MuiList-root': {
            backgroundColor: `${getThemeColor('#ffffff', '#2d2d2d')} !important`,
        },
    };

    const chromeFormWrapSx = {
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
    };
    const chromeFormFieldSx = {
        width: '100%',
        mb: 1.5,
        ...comparisonFieldSx(),
    };
    const chromeFormGridSx = {
        display: 'grid',
        width: '100%',
        gap: 1.5,
        alignItems: 'start',
        gridTemplateColumns: {
            xs: 'repeat(2, minmax(80px, 1fr))',
            sm: 'repeat(4, minmax(80px, 1fr))',
            md: 'repeat(6, minmax(80px, 1fr))',
            lg: 'repeat(8, minmax(80px, 1fr))',
        },
    };
    const chromeFormCellSx = (span) => ({
        ...chromeFormFieldSx,
        mb: 0,
        gridColumn: span,
        minWidth: 80,
        width: '100%',
    });

    const getUsers = async () => {
        const cached = adminUserEmailsCacheRef.current;
        if (cached && cached.length > 0) {
            setUsers(cached);
            return;
        }

        const gwUrl = `${global.ApiUrl}${global.Endpoints.GrainWave}`;
        const headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + global.gw.token,
        };

        const normalizeEmailRows = (rows) => {
            const list = Array.isArray(rows) ? rows : [];
            return list
                .map((x) => ({
                    email: x.email ?? x.Email ?? '',
                }))
                .filter((x) => x.email);
        };

        let normalized = [];
        try {
            const response = await axios({
                url: gwUrl,
                method: 'post',
                headers,
                data: {
                    query: `query AdminUserEmails { fetchAdminUserEmails { email } }`,
                },
            });
            const gqlErr = response.data?.errors;
            const rows = response.data?.data?.fetchAdminUserEmails;
            if (!gqlErr?.length && Array.isArray(rows)) {
                normalized = normalizeEmailRows(rows);
            }
        } catch {
            values.wall = '';
        }

        const list = [{ email: 'New User' }, ...normalized];
        adminUserEmailsCacheRef.current = list;
        setUsers(list);
    };

    const normalizeSiteSelectionRows = (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        return list
            .map((x) => ({
                customerSiteGuid: String(
                    x.customerSiteGuid ?? x.CustomerSiteGuid ?? x.siteId ?? x.SiteId ?? ''
                ),
                siteName: x.siteName ?? x.SiteName ?? '',
                isSelected: !!(x.isSelected ?? x.IsSelected),
            }))
            .filter((x) => x.customerSiteGuid);
    };

    const runAdminSiteListGraphQL = async (query, variables = {}) => {
        const response = await axios({
            url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + global.gw.token,
            },
            data: { query, variables },
        });
        const gqlErrors = response?.data?.errors;
        if (gqlErrors?.length) {
            console.warn('Admin site list GraphQL error:', gqlErrors);
            return null;
        }
        return response?.data?.data ?? null;
    };

    const getUserSelectedAllSites = async (email) => {
        const generation = ++sitesLoadGenerationRef.current;

        let results = null;
        const selectedQueries = [
            `
            query GetUserSelectedAllSites($email: String!) {
                getUserSelectedAllSites(email: $email) {
                    siteId
                    siteName
                    isSelected
                }
            }
            `,
            `
            query GetUserSelectedAllSites($email: String!) {
                getUserSelectedAllSites(email: $email) {
                    customerSiteGuid
                    siteName
                    isSelected
                }
            }
            `,
        ];

        for (const query of selectedQueries) {
            try {
                const data = await runAdminSiteListGraphQL(query, { email });
                const rows = data?.getUserSelectedAllSites;
                if (Array.isArray(rows)) {
                    results = rows;
                    break;
                }
            } catch {
                // try alternate field naming
            }
        }

        if (results === null) {
            try {
                const data = await runAdminSiteListGraphQL(`
                    query GetAllSites {
                        getAllSites {
                            siteId
                            siteName
                            isSelected
                        }
                    }
                `);
                const rows = data?.getAllSites;
                if (Array.isArray(rows)) {
                    results = rows.map((row) => ({ ...row, isSelected: false }));
                }
            } catch {
                results = [];
            }
        }

        if (generation !== sitesLoadGenerationRef.current) {
            return;
        }

        const normalized = normalizeSiteSelectionRows(results ?? []);
        const selectedIds = normalized.filter((x) => x.isSelected).map((x) => x.customerSiteGuid);
        setUserSelectedAllSites(normalized);
        setAdminSelectedSiteIds(selectedIds);
        adminSelectedSiteIdsRef.current = selectedIds;
    };

    const handleAdminSiteSelectionChange = (nextIds) => {
        const ids = (Array.isArray(nextIds) ? nextIds : [])
            .map((id) => String(id))
            .filter(Boolean);
        const prev = adminSelectedSiteIdsRef.current;
        const unchanged =
            ids.length === prev.length &&
            ids.every((id, index) => id === prev[index]);
        if (unchanged) {
            return;
        }
        invalidateAdminSitesLoad();
        setAdminSelectedSiteIds(ids);
        adminSelectedSiteIdsRef.current = ids;
    };

    /** Loads Admin modal fields for an existing user (same as choosing them in the dropdown). */
    const loadAdminUserProfileByEmail = async (sel) => {
        if (!sel || sel === 'New User') return;
        const profileGeneration = ++adminProfileLoadGenerationRef.current;
        const gwUrl = `${global.ApiUrl}${global.Endpoints.GrainWave}`;
        const hdrs = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + global.gw.token,
        };
        try {
            const [fpRes, ridRes] = await Promise.all([
                axios({
                    url: gwUrl,
                    method: 'post',
                    headers: hdrs,
                    data: {
                        query: `query FetchProfile($email: String!) {
                            fetchProfile(email: $email) {
                                first
                                last
                                email
                                cell
                            }
                        }`,
                        variables: { email: sel },
                    },
                }),
                axios({
                    url: gwUrl,
                    method: 'post',
                    headers: hdrs,
                    data: {
                        query: `query R($t: String!) { fetchUserGrainWaveRoleId(targetUserEmail: $t) }`,
                        variables: { t: sel },
                    },
                }),
            ]);

            if (profileGeneration !== adminProfileLoadGenerationRef.current) {
                return;
            }

            const fpErr = fpRes.data?.errors;
            const fp = fpRes.data?.data?.fetchProfile ?? fpRes.data?.data?.FetchProfile;
            if (fpErr?.length || !fp) {
                clearForm();
                setValues((v) => ({ ...v, isNewUser: true, grainWaveRoleId: 2 }));
                await getUserSelectedAllSites('');
                setSenderEmail(global.gw.email);
                return;
            }

            let roleId = 2;
            const ridErr = ridRes.data?.errors;
            const rid = ridRes.data?.data?.fetchUserGrainWaveRoleId ?? ridRes.data?.data?.FetchUserGrainWaveRoleId;
            if (!ridErr?.length && rid != null && rid !== '') {
                const n = Number(rid);
                if (!Number.isNaN(n)) {
                    roleId = n;
                }
            }

            const email = fp.email ?? fp.Email ?? '';
            const first = fp.first ?? fp.First ?? '';
            const last = fp.last ?? fp.Last ?? '';
            const phone = fp.cell ?? fp.Cell ?? '';

            if (profileGeneration !== adminProfileLoadGenerationRef.current) {
                return;
            }

            setValues({
                email,
                first,
                last,
                phone,
                isNewUser: false,
                sites: [],
                wall: null,
                grainWaveRoleId: roleId,
            });
            setSenderEmail(email);
            await getUserSelectedAllSites(email);
            if (profileGeneration !== adminProfileLoadGenerationRef.current) {
                return;
            }
            setForceUpdate((n) => n + 1);
        } catch {
            if (profileGeneration !== adminProfileLoadGenerationRef.current) {
                return;
            }
            clearForm();
            setValues((v) => ({ ...v, isNewUser: true, grainWaveRoleId: 2 }));
            await getUserSelectedAllSites('');
            setSenderEmail(global.gw.email);
        }
    };

    /**
     * @param {object} [options]
     * @param {boolean} [options.fromAdminModal] - When true, always include grainWaveRoleId for admins (must not rely on
     *   `isAdminModalOpen` after `await`: the Update button sets the modal closed before encrypt/password work finishes).
     */
    const updateUserProfile = async (options = {}) => {
        const fromAdminModal = options.fromAdminModal === true;
        const saveGeneration = ++adminProfileSaveGenerationRef.current;

        // Profile modal: never send role by default. Admin modal: pass fromAdminModal so payload is correct after await.
        const shouldIncludeGrainWaveRole = fromAdminModal
            ? isAdmin
            : (isAdmin && isAdminModalOpen[0]);

        // Update closes the modal immediately. clearForm empties the site checklist while
        // password encryption is still running, so capture the form and checks first.
        const profilePayload = { ...values };
        const emailForUpdate = String(senderEmail || '').trim();
        const siteCatalog = userSelectedAllSites;
        const selectedIdsSnapshot = [...adminSelectedSiteIdsRef.current];
        const plainWall = profilePayload.wall;

        if (plainWall && plainWall !== "") {
            profilePayload.wall = await global.encryptMessageOutAsync(plainWall, 4096, 4096, 512);
        }

        if (saveGeneration !== adminProfileSaveGenerationRef.current) {
            return;
        }

        // Server decrypts the password with adminEmail's UserToken keys. Required for new users
        // (senderEmail stays the admin email) and when editing another user's password.
        if (isAdmin && profilePayload.wall && profilePayload.wall !== "" && (profilePayload.isNewUser || emailForUpdate !== global.gw.email)) {
            profilePayload.adminEmail = global.gw.email;
        }

        delete profilePayload.sites;
        if (fromAdminModal && siteCatalog.length > 0) {
            const allowedSiteIds = new Set(
                siteCatalog
                    .map((s) => String(s.customerSiteGuid ?? s.CustomerSiteGuid ?? s.siteId ?? s.SiteId ?? '').trim().toLowerCase())
                    .filter(Boolean)
            );
            profilePayload.selectedSiteIds = selectedIdsSnapshot
                .map((id) => String(id).trim())
                .filter((id) => id && allowedSiteIds.has(id.toLowerCase()));
        } else {
            delete profilePayload.selectedSiteIds;
        }
        if (shouldIncludeGrainWaveRole) {
            profilePayload.grainWaveRoleId = Number(profilePayload.grainWaveRoleId) || 2;
        } else {
            delete profilePayload.grainWaveRoleId;
        }
        axios({
            url: `${global.ApiUrl}${global.Endpoints.GrainWave}`,
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Bearer " + global.gw.token,
            },
            data: {
                query: `
                        mutation UpdateUserProfile($email: String!, $json: String!) {
                            updateUserProfile(email: $email, json: $json)
                        }
                    `,
                variables: {
                    email: emailForUpdate,
                    json: JSON.stringify(profilePayload),
                }
            }
        }).then((response) => {
            if (saveGeneration !== adminProfileSaveGenerationRef.current) {
                return;
            }
            const gqlErrors = response?.data?.errors;
            const ok = response?.data?.data?.updateUserProfile != null && !gqlErrors?.length;
            if (fromAdminModal && ok) {
                adminUserEmailsCacheRef.current = null;
            }
        }).catch(function () {
        });
    };

    const temputechIcon = React.useMemo(() => (
        <img
            key={`temputech-icon-${currentTheme}-${forceUpdate}`}
            src={Assets.Images.TemputechMenuLogo}
            alt="Temputech"
            style={{
                width: "164px",
                maxHeight: "36px",
                height: "auto",
                display: "block",
                objectFit: "contain",
                objectPosition: "left center",
                cursor: "pointer",
                // In light mode, filter to convert orangish colors to black
                filter: currentTheme === 'light'
                    ? 'brightness(0) saturate(100%)'
                    : 'none',
            }}
        />
    ), [currentTheme, forceUpdate]);

    const timeElement = (
        <div
            style={{
                margin: 'auto -10px'
            }}
        >
            <div
                style={{
                    fontSize: 12,
                    fontFamily: Assets.Fonts.MyriadProBoldName,
                    opacity: .95,
                    textAlign: 'right'
                }}
            >
                1:00pm
            </div>
            <div
                style={{
                    fontSize: 8,
                    fontFamily: Assets.Fonts.MyriadProRegularName,
                    opacity: .75,
                    textAlign: 'right'
                }}
            >
                Friday 12th, 2022
            </div>
        </div>
    );

    const dividerElement = (
        <MuiMaterials.Divider
            sx={{
                margin: 0,
                height: 0,
                width: 40,
                WebkitFlexShrink: 0,
                MsFlexNegative: 0,
                flexShrink: 0,
                borderWidth: '0px',
                borderStyle: 'solid',
                borderColor: 'silver',
                borderBottomWidth: 'thin',
                transform: 'rotate(90deg)'
            }}
        />
    );

    const userNameElement = (
        <div
            style={{
                margin: 'auto 15px auto -10px'
            }}
        >
            <div
                style={{
                    fontSize: 12,
                    fontFamily: Assets.Fonts.MyriadProBoldName,
                    opacity: .95,
                    textAlign: 'right'
                }}
            >
                {global.gw.email}
            </div>
            <div
                style={{
                    fontSize: 8,
                    fontFamily: Assets.Fonts.MyriadProRegularName,
                    opacity: .75,
                    textAlign: 'right'
                }}
            >
                Administrator
            </div>
        </div>
    );

    const userIconElement = (popupState) => {
        // Get initials from user's first and last name if available, otherwise fallback to email
        const getInitials = () => {
            // Priority 1: Use global user profile data (persists across navigation)
            if (global.gw.userProfile && global.gw.userProfile.first && global.gw.userProfile.last) {
                return `${global.gw.userProfile.first.charAt(0).toUpperCase()}${global.gw.userProfile.last.charAt(0).toUpperCase()}`;
            }

            // Priority 2: Use local form values (if available)
            if (values.first && values.last) {
                return `${values.first.charAt(0).toUpperCase()}${values.last.charAt(0).toUpperCase()}`;
            }

            // Priority 3: Fallback to email-based initials if no profile data
            const email = global.gw.email;
            if (!email) return 'U';
            const parts = email.split('@');
            if (parts.length >= 2) {
                const username = parts[0];
                const domain = parts[1].split('.')[0];
                return `${username.charAt(0).toUpperCase()}${domain.charAt(0).toUpperCase()}`;
            }
            return email.charAt(0).toUpperCase();
        };

        const initials = getInitials();

        return (
            <MuiMaterials.Box
                className="userIconElement"
                sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderRadius: '4px',
                    border: `1px solid ${getThemeColor('#cccccc', BRAND_GOLD_BORDER)}`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                    boxShadow: 'none',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                        borderColor: getThemeColor('#999999', BRAND_GOLD),
                    },
                }}
                aria-label="menu"
                {...bindTrigger(popupState)}
            >
                <MuiMaterials.Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 800,
                        fontFamily: Assets.Fonts.MyriadProBoldName,
                        textAlign: 'center',
                        lineHeight: 1,
                        letterSpacing: '0.4px',
                        ...brandGoldMetallicTextSx({ light: global.gw.theme === 'light' }),
                    }}
                >
                    {initials}
                </MuiMaterials.Typography>
            </MuiMaterials.Box>
        );
    };

    const renderFeedbackModal = () => {
        const handleClose = () => {
            setFeedbackText('');
            setIsFeedbackModalOpen([false]);
        };
        return (
            <ModalChrome
                open={isFeedbackModalOpen[0]}
                onClose={handleClose}
                title="Feedback"
                actions={
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={outlinedActionButtonSx()}
                        onClick={() => {
                            sendUserFeedback(feedbackText);
                            handleClose();
                        }}
                    >
                        Send
                    </MuiMaterials.Button>
                }
            >
                <ModalBody>
                    <ModalContentTile sx={{ overflowY: 'auto' }}>
                        <MuiMaterials.Box sx={chromeFormWrapSx}>
                            <MuiMaterials.TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={10}
                                label="Comments or Suggestions"
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                sx={comparisonFieldSx()}
                            />
                        </MuiMaterials.Box>
                    </ModalContentTile>
                </ModalBody>
            </ModalChrome>
        );
    };

    const renderProfileModal = () => {
        const handleClose = () => {
            clearForm();
            setIsProfileModalOpen([false]);
        };
        return (
            <ModalChrome
                open={isProfileModalOpen[0]}
                onClose={handleClose}
                title="Profile"
                actions={
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={outlinedActionButtonSx()}
                        onClick={() => {
                            if (validate()) {
                                updateUserProfile();
                                handleClose();
                            }
                        }}
                    >
                        Update
                    </MuiMaterials.Button>
                }
            >
                <ModalBody>
                    <ModalContentTile
                        sx={{
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            pt: 2,
                        }}
                    >
                        <MuiMaterials.Box component="form" noValidate sx={chromeFormGridSx}>
                            <MuiMaterials.TextField
                                size="small"
                                value={values.email}
                                label="Email"
                                variant="outlined"
                                sx={chromeFormCellSx({ xs: '1 / -1', md: 'span 4' })}
                                onChange={async (e) => {
                                    values.email = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.email && { error: true, helperText: errors.email })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.first}
                                label="First Name"
                                variant="outlined"
                                sx={chromeFormCellSx('span 2')}
                                onChange={async (e) => {
                                    values.first = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.first && { error: true, helperText: errors.first })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.last}
                                label="Last Name"
                                variant="outlined"
                                sx={chromeFormCellSx('span 2')}
                                onChange={async (e) => {
                                    values.last = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.last && { error: true, helperText: errors.last })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.phone}
                                label="Phone"
                                variant="outlined"
                                sx={chromeFormCellSx('span 2')}
                                onChange={async (e) => {
                                    values.phone = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.phone && { error: true, helperText: errors.phone })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                defaultValue=""
                                label="Password"
                                variant="outlined"
                                type="password"
                                name="wall"
                                sx={chromeFormCellSx('span 2')}
                                onChange={async (e) => {
                                    values.wall = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.wall && { error: true, helperText: errors.wall })}
                            />
                        </MuiMaterials.Box>
                    </ModalContentTile>
                </ModalBody>
            </ModalChrome>
        );
    };

    const renderAdminModal = () => {
        if (!isAdmin) return null;
        const roleOptions = grainWaveRoles.length > 0 ? grainWaveRoles : DEFAULT_GRAIN_WAVE_ROLES;
        const handleClose = () => {
            clearForm();
            setIsAdminModalOpen([false]);
        };
        const selectMenuProps = withScrollHintMenuProps({
            PaperProps: {
                sx: adminModalMenuPaperSx,
            },
        });
        return (
            <ModalChrome
                open={isAdminModalOpen[0]}
                onClose={handleClose}
                title="Admin"
                actions={
                    <MuiMaterials.Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={outlinedActionButtonSx()}
                            onClick={() => setIsSitesAndRolesModalOpen([true])}
                        >
                            Sites & Roles
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={outlinedActionButtonSx()}
                            onClick={() => {
                                if (validate()) {
                                    updateUserProfile({ fromAdminModal: true });
                                    handleClose();
                                }
                            }}
                        >
                            Update
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                }
            >
                <ModalBody>
                    <ModalContentTile
                        sx={{
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            pt: 2,
                        }}
                    >
                        <MuiMaterials.Box
                            component="form"
                            noValidate
                            sx={chromeFormGridSx}
                        >
                            <MuiMaterials.FormControl
                                size="small"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: { xs: '1 / -1', md: 'span 4' },
                                    minWidth: 80,
                                    width: '100%',
                                }}
                            >
                                <MuiMaterials.InputLabel id="admin-user-label">User</MuiMaterials.InputLabel>
                                <MuiMaterials.Select
                                    labelId="admin-user-label"
                                    label="User"
                                    displayEmpty
                                    value={
                                        values.isNewUser
                                            ? 'New User'
                                            : (users.some((u) => u.email === senderEmail) ? senderEmail : '')
                                    }
                                    renderValue={(selected) => {
                                        if (selected === '' || selected == null) {
                                            return (
                                                <span style={{ opacity: 0.65 }}>
                                                    Select user…
                                                </span>
                                            );
                                        }
                                        return selected;
                                    }}
                                    MenuProps={selectMenuProps}
                                    onChange={async (e) => {
                                        const sel = e.target.value;
                                        if (sel === 'New User') {
                                            clearForm();
                                            setValues((v) => ({ ...v, isNewUser: true, grainWaveRoleId: 2 }));
                                            setSenderEmail(global.gw.email);
                                            await getUserSelectedAllSites('');
                                            return;
                                        }
                                        await loadAdminUserProfileByEmail(sel);
                                    }}
                                >
                                    {renderUsers()}
                                </MuiMaterials.Select>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.TextField
                                size="small"
                                value={values.email}
                                label="Email"
                                variant="outlined"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: { xs: '1 / -1', md: 'span 4' },
                                    minWidth: 80,
                                    width: '100%',
                                }}
                                onChange={async (e) => {
                                    values.email = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.email && { error: true, helperText: errors.email })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.first}
                                label="First Name"
                                variant="outlined"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                }}
                                onChange={async (e) => {
                                    values.first = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.first && { error: true, helperText: errors.first })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.last}
                                label="Last Name"
                                variant="outlined"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                }}
                                onChange={async (e) => {
                                    values.last = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.last && { error: true, helperText: errors.last })}
                            />
                            <MuiMaterials.TextField
                                size="small"
                                value={values.phone}
                                label="Phone"
                                variant="outlined"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                }}
                                onChange={async (e) => {
                                    values.phone = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.phone && { error: true, helperText: errors.phone })}
                            />
                            <MuiMaterials.FormControl
                                size="small"
                                variant="outlined"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                }}
                            >
                                <MuiMaterials.InputLabel id="admin-role-label">Role</MuiMaterials.InputLabel>
                                <MuiMaterials.Select
                                    labelId="admin-role-label"
                                    label="Role"
                                    value={Number(values.grainWaveRoleId) || 2}
                                    MenuProps={selectMenuProps}
                                    onChange={(ev) => {
                                        const n = Number(ev.target.value);
                                        setValues((v) => ({ ...v, grainWaveRoleId: n }));
                                    }}
                                >
                                    {roleOptions.map((r) => (
                                        <MuiMaterials.MenuItem key={r.grainWaveRoleId} value={Number(r.grainWaveRoleId)}>
                                            {r.name}
                                        </MuiMaterials.MenuItem>
                                    ))}
                                </MuiMaterials.Select>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.TextField
                                size="small"
                                defaultValue=""
                                label="Password"
                                variant="outlined"
                                type="password"
                                name="wall"
                                sx={{
                                    ...chromeFormFieldSx,
                                    mb: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                }}
                                onChange={async (e) => {
                                    values.wall = e.currentTarget.value;
                                    validate();
                                }}
                                {...(errors.wall && { error: true, helperText: errors.wall })}
                            />
                            <MuiMaterials.Box sx={{ gridColumn: '1 / -1', minWidth: 0, width: '100%' }}>
                                <Components.MultipleSiteSelect
                                    names={userSelectedAllSites}
                                    value={adminSelectedSiteIds}
                                    onChange={handleAdminSiteSelectionChange}
                                />
                            </MuiMaterials.Box>
                        </MuiMaterials.Box>
                    </ModalContentTile>
                </ModalBody>
            </ModalChrome>
        );
    };

    const renderUsers = () => {
        return users.map((u) => (
            <MuiMaterials.MenuItem key={u.email} value={u.email}>
                {u.email}
            </MuiMaterials.MenuItem>
        ));
    };

    const renderUserSites = () => {
        return (userSelectedAllSites.map((s) => (
            <MuiMaterials.MenuItem value={s}>{s.siteName}</MuiMaterials.MenuItem>
        )));
    };

    const renderSitesAndRolesModal = () => {
        if (!isAdmin) return null;
        return (
            <Components.SitesAndRolesModal
                isModalOpen={isSitesAndRolesModalOpen}
                setIsModalOpen={setIsSitesAndRolesModalOpen}
            />
        );
    };

    const renderSiteFilterModal = () => {
        return (
            <Components.SiteFilter
                isModalOpen={isSiteFilterModalOpen}
                setIsModalOpen={setIsSiteFilterModalOpen}
            />
        );
    };

    const renderViewSiteIssuesModal = () => {
        if (!isAdmin) return null;
        return (
            <Components.ViewSiteIssuesModal
                isModalOpen={isViewSiteIssuesModalOpen}
                setIsModalOpen={setIsViewSiteIssuesModalOpen}
                userSelectedAllSites={userSelectedAllSites}
                initialSiteGuid={viewSiteIssuesSiteGuid}
                filterToSiteGuid={viewSiteIssuesSiteGuid}
            />
        );
    };

    const renderSiteGradesModal = () => {
        return (
            <Components.SiteGradesModal
                isModalOpen={isSiteGradesModalOpen}
                setIsModalOpen={setIsSiteGradesModalOpen}
                initialSiteGuid={siteGradesInitialGuid}
            />
        );
    };

    const renderSiteInventoryModal = () => {
        return (
            <Components.SiteInventoryModal
                isModalOpen={isSiteInventoryModalOpen}
                setIsModalOpen={setIsSiteInventoryModalOpen}
                singleBinContext={siteInventorySingleBin}
            />
        );
    };

    const renderSiteVersioningModal = () => {
        return (
            <Components.SiteVersioningModal
                isModalOpen={isSiteVersioningModalOpen}
                setIsModalOpen={setIsSiteVersioningModalOpen}
                filterToSiteGuid={siteVersioningSiteGuid}
            />
        );
    };

    const renderSitesScanningModal = () => {
        if (!isAdmin) return null;
        return (
            <Components.SitesScanningModal
                isModalOpen={isSitesScanningModalOpen}
                setIsModalOpen={setIsSitesScanningModalOpen}
                filterToSiteGuid={sitesScanningSiteGuid}
            />
        );
    };

    const renderRemoteModal = () => {
        if (!isAdmin) return null;
        return (
            <Components.RemoteAccessModal
                isModalOpen={isRemoteModalOpen}
                setIsModalOpen={setIsRemoteModalOpen}
                filterToSiteGuid={remoteSiteGuid}
            />
        );
    };

    const renderSiteComparisonModal = () => {
        return (
            <Components.SiteComparisonModal
                isModalOpen={isSiteComparisonModalOpen}
                setIsModalOpen={setIsSiteComparisonModalOpen}
                initialSiteGuid={siteComparisonInitialGuid}
            />
        );
    };

    // Restored tokens are validated async; show login until auth is confirmed or token is cleared.
    if (global.gw.token && !authReady) {
        return (
            <MuiMaterials.Box
                sx={{
                    ...getSiteStyles().sitesContainer,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Spinner size="page" />
            </MuiMaterials.Box>
        );
    }

    if (!global.gw.token || !global.gw.isAuthenticated) {
        // If not authenticated, show login page with AppBar
        return (
            <MuiMaterials.Box
                sx={{
                    ...getSiteStyles().sitesContainer,
                }}
            >
                <MuiMaterials.CssBaseline />
                <MuiMaterials.AppBar
                    position="fixed"
                    color="inherit"
                    elevation={0}
                    sx={{
                        ...getSiteStyles().menuBar.appBar,
                        zIndex: 1000, // Ensure AppBar is above other content
                    }}
                >
                    <MuiMaterials.Toolbar
                        disableGutters
                        sx={getSiteStyles().menuBar.toolBar}>
                        {temputechIcon}
                    </MuiMaterials.Toolbar>
                </MuiMaterials.AppBar>
                <main
                    style={{
                        flex: '1 1 auto',
                        width: '100%',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <MuiMaterials.Box
                        sx={{
                            flex: 1,
                            width: '100%',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            boxSizing: 'border-box',
                        }}
                    >
                        <MuiMaterials.Box
                            id={"navigationBar"}
                            sx={{
                                display: "flex",
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100%",
                                flexDirection: "column",
                                alignItems: "stretch",
                                zIndex: 999, // Below AppBar
                            }}
                        />
                        {/* Pull-to-refresh indicator for login page - only show when threshold is met */}
                        {/* Moved outside outletContainer to avoid transform containing block issues */}
                        {pullToRefresh.isPulling && pullToRefresh.pullDistance >= 200 && !isFullscreenAppRoute(location.pathname) && (() => {
                            // Calculate spinner position using actual outletContainer position
                            const appBarHeight = 64;
                            const outletContainer = document.getElementById('outletContainer');
                            let actualOutletTop = appBarHeight + pullToRefresh.pullDistance;
                            if (outletContainer) {
                                const rect = outletContainer.getBoundingClientRect();
                                actualOutletTop = rect.top;
                            }
                            const grayAreaStart = appBarHeight;
                            const grayAreaEnd = actualOutletTop;
                            const grayAreaHeight = grayAreaEnd - grayAreaStart;
                            const calculatedTop = grayAreaStart + (grayAreaHeight / 2);
                            
                            return (
                                <MuiMaterials.Box
                                    ref={spinnerBoxRef}
                                    style={{
                                        position: "fixed",
                                        top: `${calculatedTop}px`,
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 1001,
                                        pointerEvents: "none",
                                        transition: "none",
                                    }}
                                >
                                <MuiMaterials.Box
                                    sx={{ opacity: Math.min(pullToRefresh.pullDistance / 80, 1) }}
                                >
                                    <Spinner size={pullToRefresh.pullDistance >= 80 ? "32" : "24"} />
                                </MuiMaterials.Box>
                                {pullToRefresh.pullDistance >= 80 && (
                                    <MuiMaterials.Typography
                                        sx={{
                                            marginTop: "8px",
                                            fontSize: "18px",
                                            color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                            fontFamily: Assets.Fonts.MyriadProRegularName,
                                        }}
                                    >
                                        Release to refresh
                                    </MuiMaterials.Typography>
                                )}
                                </MuiMaterials.Box>
                            );
                        })()}
                        <MuiMaterials.Box
                            id={"outletContainer"}
                            sx={{
                                marginTop: "64px",
                                overflow: "hidden",
                                display: "flex",
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                height: isLogoutNavigation ? "calc(100vh + 110px)" : "calc(100vh - 64px)",
                                position: "relative",
                                backgroundColor: getWindowChromeThemeColor('#e8e8e8', '#1a1a1a'),
                            }}
                        >
                            <Components.EmailLogin />
                        </MuiMaterials.Box>
                    </MuiMaterials.Box>
                </main>
            </MuiMaterials.Box>
        );
    }

    return (
        <MuiMaterials.Box
            sx={{
                ...getSiteStyles().sitesContainer,
            }}
        >
            <MuiMaterials.CssBaseline />
            <MuiMaterials.AppBar
                position="relative"
                color="inherit"
                elevation={0}
                sx={getSiteStyles().menuBar.appBar}
            >
                <MuiMaterials.Toolbar
                    disableGutters
                    sx={getSiteStyles().menuBar.toolBar}>
                    {temputechIcon}
                    <PopupState
                        variant="popover">
                        {(popupState) => (
                            <>
                                <MuiMaterials.Box
                                    variant="h6"
                                    component="div"
                                    sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
                                >
                                    {userIconElement(popupState)}
                                </MuiMaterials.Box>
                                <MuiMaterials.Menu 
                                    {...bindMenu(popupState)}
                                    key={`menu-${currentTheme}-${forceUpdate}`}
                                    {...withScrollHintMenuProps({
                                        PaperProps: {
                                            style: {
                                                backgroundColor: currentTheme === 'light' ? '#ffffff' : '#2d2d2d',
                                                color: currentTheme === 'light' ? '#333333' : '#f4cc8e',
                                            },
                                            sx: {
                                                backgroundColor: `${currentTheme === 'light' ? '#ffffff' : '#2d2d2d'} !important`,
                                                color: currentTheme === 'light' ? '#333333' : '#f4cc8e',
                                                border: `1px solid ${currentTheme === 'light' ? '#cccccc' : '#f4cc8e4d'}`,
                                                borderRadius: '4px',
                                                boxShadow: currentTheme === 'light'
                                                    ? '0 8px 24px rgba(0, 0, 0, 0.12)'
                                                    : '0 8px 24px rgba(0, 0, 0, 0.55)',
                                                minWidth: 220,
                                                maxWidth: 'min(90vw, 480px)',
                                                '& ul, & .MuiList-root': {
                                                    backgroundColor: `${currentTheme === 'light' ? '#ffffff' : '#2d2d2d'} !important`,
                                                    padding: '6px 0',
                                                },
                                                '& .MuiMenuItem-root': {
                                                    color: `${currentTheme === 'light' ? '#333333' : '#f4cc8e'} !important`,
                                                    backgroundColor: 'transparent !important',
                                                    justifyContent: 'flex-start',
                                                    textAlign: 'left',
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    lineHeight: 1.5,
                                                    padding: '8px 16px',
                                                    minHeight: 40,
                                                    '&:hover': {
                                                        backgroundColor: `${currentTheme === 'light' ? '#f5f5f5' : '#3d3d3d'} !important`,
                                                    }
                                                },
                                                '& .MuiDivider-root': {
                                                    border: 'none',
                                                    height: '1px',
                                                    mx: 2,
                                                    my: 0.5,
                                                    backgroundColor: currentTheme === 'light'
                                                        ? 'rgba(0, 0, 0, 0.12)'
                                                        : 'rgba(244, 204, 142, 0.35)',
                                                },
                                            },
                                        },
                                    }, currentTheme)}
                                >
                                    {(() => {
                                        const mainMenuItemSx = {
                                            color: `${currentTheme === 'light' ? '#333333' : '#f4cc8e'} !important`,
                                            backgroundColor: 'transparent !important',
                                            '&:hover': {
                                                backgroundColor: `${currentTheme === 'light' ? '#f5f5f5' : '#3d3d3d'} !important`,
                                            },
                                        };
                                        const mainMenuDividerSx = {
                                            border: "none",
                                            height: "1px",
                                            mx: 2,
                                            my: 0.5,
                                            backgroundColor: currentTheme === "light"
                                                ? "rgba(0, 0, 0, 0.12)"
                                                : "rgba(244, 204, 142, 0.35)",
                                        };
                                        const userItems = [
                                            isAdmin
                                                ? {
                                                    key: `admin-${currentTheme}-${forceUpdate}`,
                                                    label: 'Admin',
                                                    onClick: () => {
                                                        popupState.close();
                                                        clearForm();
                                                        getUsers();
                                                        setIsAdminModalOpen([true]);
                                                        const initialEmail = String(global.gw.email || '').trim();
                                                        if (initialEmail) {
                                                            void loadAdminUserProfileByEmail(initialEmail);
                                                        }
                                                    },
                                                }
                                                : null,
                                            {
                                                key: `feedback-${currentTheme}-${forceUpdate}`,
                                                label: 'Feedback',
                                                onClick: () => {
                                                    popupState.close();
                                                    setFeedbackText('');
                                                    setIsFeedbackModalOpen([true]);
                                                },
                                            },
                                            isAdmin
                                                ? {
                                                    key: `manufacturing-${currentTheme}-${forceUpdate}`,
                                                    label: 'Manufacturing',
                                                    onClick: () => {
                                                        popupState.close();
                                                        navigate('/manufacturing');
                                                    },
                                                }
                                                : null,
                                            {
                                                key: `profile-${currentTheme}-${forceUpdate}`,
                                                label: 'Profile',
                                                onClick: () => {
                                                    popupState.close();
                                                    const ctask = () => createApiEndpoint().fetchProfile("direct");
                                                    ctask().then((response) => {
                                                        if (response) {
                                                            values.email = response.data.fetchProfile.email;
                                                            values.first = response.data.fetchProfile.first;
                                                            values.last = response.data.fetchProfile.last;
                                                            values.phone = response.data.fetchProfile.cell;
                                                            if (!global.gw.userProfile) {
                                                                global.gw.userProfile = {};
                                                            }
                                                            global.gw.userProfile.first = response.data.fetchProfile.first;
                                                            global.gw.userProfile.last = response.data.fetchProfile.last;
                                                            global.gw.userProfile.email = response.data.fetchProfile.email;
                                                            global.gw.userProfile.phone = response.data.fetchProfile.cell;
                                                            try {
                                                                localStorage.setItem('grainwave_userProfile', JSON.stringify(global.gw.userProfile));
                                                            } catch (error) {
                                                                console.error('Error saving userProfile to localStorage:', error);
                                                            }
                                                            setIsProfileModalOpen([true]);
                                                        }
                                                    });
                                                },
                                            },
                                        ]
                                            .filter(Boolean)
                                            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                                        const siteItems = getMainMenuSiteItems();
                                        const openMainMenuSiteItem = (itemId) => {
                                            popupState.close();
                                            if (itemId === SITE_WINDOW_IDS.filter) {
                                                values.email = "";
                                                values.first = "";
                                                values.last = "";
                                                values.phone = "";
                                                values.wall = "";
                                                openSiteWindow(SITE_WINDOW_IDS.filter);
                                                return;
                                            }
                                            if (itemId === SITE_WINDOW_IDS.inventory) {
                                                setSiteInventorySingleBin(null);
                                                openSiteWindow(SITE_WINDOW_IDS.inventory);
                                                return;
                                            }
                                            if (itemId === SITE_WINDOW_IDS.versioning) {
                                                openSiteWindow(SITE_WINDOW_IDS.versioning);
                                                return;
                                            }
                                            if (itemId === SITE_WINDOW_IDS.sitesScanning) {
                                                openSiteWindow(SITE_WINDOW_IDS.sitesScanning);
                                                return;
                                            }
                                            if (itemId === SITE_WINDOW_IDS.sites3d || itemId === SITE_WINDOW_IDS.bin) {
                                                if (global.gw.weatherStationAbortController) {
                                                    global.gw.weatherStationAbortController.abort();
                                                    global.gw.weatherStationAbortController = null;
                                                }
                                                if (global.gw.dashboardInterval) {
                                                    clearInterval(global.gw.dashboardInterval);
                                                    global.gw.dashboardInterval = null;
                                                }
                                                openSiteWindow(itemId);
                                                return;
                                            }
                                            openSiteWindow(itemId);
                                        };

                                        const themeLabel = (!global.gw.theme || global.gw.theme === 'dark')
                                            ? 'Light Mode'
                                            : 'Dark Mode';
                                        const appItems = [
                                            {
                                                key: `theme-toggle-${currentTheme}-${forceUpdate}`,
                                                label: themeLabel,
                                                displayLabel: (!global.gw.theme || global.gw.theme === 'dark')
                                                    ? '☀️ Light Mode'
                                                    : '🌙 Dark Mode',
                                                onClick: () => {
                                                    popupState.close();
                                                    if (!global.gw.theme) global.gw.theme = 'dark';
                                                    global.gw.theme = global.gw.theme === 'dark' ? 'light' : 'dark';
                                                    setCurrentTheme(global.gw.theme);
                                                    try {
                                                        localStorage.setItem('grainwave_theme', global.gw.theme);
                                                    } catch (error) {
                                                        console.error('Error saving theme to localStorage:', error);
                                                    }
                                                    if (global.gw.theme === 'light') {
                                                        document.body.classList.add('light-mode');
                                                    } else {
                                                        document.body.classList.remove('light-mode');
                                                    }
                                                    global.gw.themeChanged = Date.now();
                                                    window.dispatchEvent(new CustomEvent('themeChanged'));
                                                    setForceUpdate(prev => prev + 1);
                                                },
                                            },
                                            {
                                                key: `logout-${currentTheme}-${forceUpdate}`,
                                                label: 'Logout',
                                                displayLabel: 'Logout',
                                                onClick: () => {
                                                    popupState.close();
                                                    setIsLogoutNavigation(true);
                                                    clearToken();
                                                    navigate("/emailLogin", { replace: true });
                                                },
                                            },
                                        ]
                                            .filter(Boolean)
                                            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                                        return (
                                            <>
                                                {userItems.map((item) => (
                                                    <MuiMaterials.MenuItem
                                                        key={item.key}
                                                        sx={mainMenuItemSx}
                                                        onClick={item.onClick}
                                                    >
                                                        {item.label}
                                                    </MuiMaterials.MenuItem>
                                                ))}
                                                <MuiMaterials.Divider variant="middle" key={`divider-user-site-${currentTheme}-${forceUpdate}`} sx={mainMenuDividerSx} />
                                                {siteItems.map((item) => (
                                                    <MuiMaterials.MenuItem
                                                        key={`site-${item.id}-${currentTheme}-${forceUpdate}`}
                                                        sx={mainMenuItemSx}
                                                        onClick={() => openMainMenuSiteItem(item.id)}
                                                    >
                                                        {item.label}
                                                    </MuiMaterials.MenuItem>
                                                ))}
                                                <MuiMaterials.Divider variant="middle" key={`divider-site-app-${currentTheme}-${forceUpdate}`} sx={mainMenuDividerSx} />
                                                {appItems.map((item) => (
                                                    <MuiMaterials.MenuItem
                                                        key={item.key}
                                                        sx={mainMenuItemSx}
                                                        onClick={item.onClick}
                                                    >
                                                        {item.displayLabel}
                                                    </MuiMaterials.MenuItem>
                                                ))}
                                            </>
                                        );
                                    })()}
                                </MuiMaterials.Menu>
                            </>
                        )}
                    </PopupState>
                </MuiMaterials.Toolbar>
            </MuiMaterials.AppBar>
            <main
                style={{
                    flex: '1 1 0%',
                    width: '100%',
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        flex: '1 1 0%',
                        minHeight: 0,
                        width: '100%',
                        minWidth: 0,
                        bgcolor: getWindowChromeThemeColor('#e8e8e8', '#1a1a1a'),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    {(() => {
                        const impersonator = global.readImpersonator();
                        if (!impersonator?.email) return null;
                        return (
                            <MuiMaterials.Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    px: 2,
                                    py: 0.75,
                                    flexShrink: 0,
                                    backgroundColor: getWindowChromeThemeColor('#fff4e0', '#3a2e1c'),
                                    borderBottom: `1px solid ${getWindowChromeThemeColor('#e0c48a', '#f4cc8e4d')}`,
                                }}
                            >
                                <MuiMaterials.Typography
                                    sx={{
                                        fontSize: 13,
                                        color: getWindowChromeThemeColor('#333333', '#f4cc8e'),
                                    }}
                                >
                                    Viewing as {global.gw.email || 'this user'}
                                </MuiMaterials.Typography>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={outlinedActionButtonSx()}
                                    onClick={() => {
                                        if (global.endImpersonation()) {
                                            window.location.assign('/sites');
                                        }
                                    }}
                                >
                                    Return to {impersonator.email}
                                </MuiMaterials.Button>
                            </MuiMaterials.Box>
                        );
                    })()}
                    <MuiMaterials.Box
                        id={"navigationBar"}
                        sx={{
                            display: "flex",
                            //position: "fixed",
                            //top: 0,
                            //left: 0,
                            //margin: "52px auto",
                            width: "100%",
                            flexDirection: "column",
                            alignItems: "stretch",
                        }}
                    />
                    {/* Pull-to-refresh indicator - only show when threshold is met */}
                    {/* Moved outside outletContainer to avoid transform containing block issues */}
                    {pullToRefresh.isPulling && pullToRefresh.pullDistance >= 200 && !isFullscreenAppRoute(location.pathname) && (() => {
                        // Calculate spinner position
                        // On authenticated pages, the gray area is between AppBar bottom and where outletContainer would be after pull
                        const appBarHeight = 64;
                        const grayAreaStart = appBarHeight;
                        // The gray area ends where the outletContainer top would be after the pull transform
                        const grayAreaEnd = appBarHeight + pullToRefresh.pullDistance;
                        const grayAreaHeight = grayAreaEnd - grayAreaStart;
                        // Center the spinner in the gray area
                        const calculatedTop = grayAreaStart + (grayAreaHeight / 2);
                        
                        return (
                            <MuiMaterials.Box
                                ref={authenticatedSpinnerBoxRef}
                                style={{
                                    position: "fixed",
                                    top: `${calculatedTop}px`,
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 1001,
                                    pointerEvents: "none",
                                    transition: "none",
                                }}
                            >
                                <Spinner size="32" />
                                <MuiMaterials.Typography
                                    sx={{
                                        marginTop: "8px",
                                        fontSize: "18px",
                                        color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                        fontFamily: Assets.Fonts.MyriadProRegularName,
                                    }}
                                >
                                    Release to refresh
                                </MuiMaterials.Typography>
                            </MuiMaterials.Box>
                        );
                    })()}
                    <MuiMaterials.Box
                        id={"outletContainer"}
                        sx={{
                            flex: '1 1 0%',
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            position: "relative",
                            WebkitOverflowScrolling: "touch",
                            overscrollBehaviorY: "contain",
                            backgroundColor: getWindowChromeThemeColor('#e8e8e8', '#1a1a1a'),
                        }}
                    >
                        <Outlet />
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </main>
            {renderProfileModal()}
            {renderFeedbackModal()}
            {renderAdminModal()}
            {renderSitesAndRolesModal()}
            {renderSiteFilterModal()}
            {renderSiteGradesModal()}
            {renderSiteComparisonModal()}
            {renderSiteInventoryModal()}
            {renderSiteVersioningModal()}
            {renderSitesScanningModal()}
            {renderRemoteModal()}
            {renderViewSiteIssuesModal()}
        </MuiMaterials.Box>
    );
};